// 🖥 방 화면공유 (발표자 1명 → 시청자 N명) — 2026-08-12 사용자 요청. 데스크톱(Tauri) 전용.
//
// 설계 원칙: 기존 1:1 보이스톡(CallDock)을 전혀 건드리지 않는다. 시그널링(/api/tpt/share/*)·문서
//   (tpt_room_shares)·컴포넌트가 모두 분리돼 있어, 이 기능이 실패해도 통화는 영향받지 않는다.
//
// 흐름
//   발표자: 화면 선택 → share/start → 방에 [TPT_CALL]{mode:'roomshare'} 카드 게시 → 1s 폴링으로
//           대기 중인 시청자를 발견하면 그 시청자 전용 RTCPeerConnection 을 만들어 offer 저장 →
//           answer 를 받으면 연결(mesh 송신). 종료 시 share/stop + 카드 갱신.
//   시청자: 카드의 [참여] → share/join → 1s 폴링으로 내 offer 를 받으면 answer 저장 → 화면 수신.
//
// ⚠️ 발표자 업로드 대역은 시청자 수에 비례한다(SFU 없음) → MAX_VIEWERS 로 상한을 둔다.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { tptAlert, humanizeError } from '../../stores/modalStore';
import { isTauri } from '../../desktop';
import { rcSendMessage, rcUpdateMessage } from '../../api/rcClient';
import { shareCard } from '../../api/roomShare';
import { buildCallMarker, callIceConfig } from '../../api/call';
import {
	shareStart, shareJoin, shareOffer, shareAnswer, shareDeny, shareLeave, shareStop, shareState,
	type ShareDoc,
} from '../../api/roomShare';
import { Icon } from '../icons';

const MAX_VIEWERS = 8;          // 발표자 업로드 보호 (mesh 송신)
// 발표자는 발표 내내 1s 폴링 유지 — 새 참여자 감지 + 서버 하트비트(updatedAt 갱신, 크래시 판정 근거).
//   시청자는 연결되면 폴링을 멈춘다. (10분 무인 정지 규칙은 "이후 참여자가 영영 연결 안 됨" 이라 폐기, 2026-08-12 점검 ③)
const POLL_MS = 1000;
const VIEWER_MISS_MAX = 15;     // 시청자 폴링 연속 실패(문서 유실·네트워크) 허용 횟수 — 초과 시 종료 안내

/** ICE 후보 수집 완료 대기 — 기존 통화와 동일한 non-trickle 방식(사내망 전제). */
function waitIce(pc: RTCPeerConnection, timeoutMs = 5000): Promise<void> {
	if (pc.iceGatheringState === 'complete') return Promise.resolve();
	return new Promise((resolve) => {
		const done = () => { pc.removeEventListener('icegatheringstatechange', check); resolve(); };
		const check = () => { if (pc.iceGatheringState === 'complete') done(); };
		pc.addEventListener('icegatheringstatechange', check);
		window.setTimeout(done, timeoutMs);
	});
}

type Mode = 'idle' | 'host' | 'viewer';

/**
 * @param solo 별도 창(/share)에서 마운트된 경우 — 자동으로 그 공유에 참여하고, 뷰어를 창 전체로 그린다.
 *   시그널링이 서버 기반이라 메인 창과 상태를 공유할 필요가 없다(창이 직접 join 한다). 2026-08-18
 */
export default function RoomShareDock({ solo }: { solo?: { shareId: string; hostName?: string } } = {}) {
	const { t } = useTranslation();
	const { authToken, userId, username, displayName } = useAuthStore();
	const [mode, setMode] = useState<Mode>('idle');
	const [shareId, setShareId] = useState('');
	const [rid, setRid] = useState('');
	const [hostName, setHostName] = useState('');
	const [viewerCount, setViewerCount] = useState(0);
	const [connecting, setConnecting] = useState(false);
	const [full, setFull] = useState(false);            // 시청자 전체화면 토글

	const streamRef = useRef<MediaStream | null>(null);        // 발표자 화면
	const pcsRef = useRef<Map<string, RTCPeerConnection>>(new Map());  // 발표자: 시청자별 연결
	const offeredRef = useRef<Set<string>>(new Set());  // 발표자: offer 가 서버에 실린 시청자 — 재참여(waiting 복귀) 판별용
	const missRef = useRef(0);                          // 시청자: 폴링 연속 실패 카운터
	const viewerPcRef = useRef<RTCPeerConnection | null>(null);        // 시청자: 발표자와의 연결
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const pollRef = useRef<number | null>(null);
	const startedAtRef = useRef(0);
	const cardIdRef = useRef('');                       // 방에 게시한 카드 메시지 id (종료 시 갱신)
	const cardByAdminRef = useRef(false);               // 🖥 카드를 서버(관리자 명의)가 대신 올렸으면 종료 갱신도 서버로 (읽기전용 방, 2026-09-04)
	const iceRef = useRef<RTCIceServer[]>([]);
	const stopRef = useRef<() => void>(() => undefined);
	const viewerTickRef = useRef<(sid: string) => Promise<void>>(async () => undefined); // 감시 폴링용 최신 참조 (stopRef 와 동일 패턴)

	const clearPoll = () => { if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; } };

	/** 전체 정리 — 발표/시청 공통. 서버 상태 반영은 호출부에서. */
	const teardown = useCallback(() => {
		clearPoll();
		for (const [, pc] of pcsRef.current) { try { pc.close(); } catch { /* */ } }
		pcsRef.current.clear();
		offeredRef.current.clear();
		missRef.current = 0;
		try { viewerPcRef.current?.close(); } catch { /* */ }
		viewerPcRef.current = null;
		try { streamRef.current?.getTracks().forEach((tr) => tr.stop()); } catch { /* */ }
		streamRef.current = null;
		if (videoRef.current) videoRef.current.srcObject = null;
		setMode('idle'); setShareId(''); setRid(''); setViewerCount(0); setConnecting(false); setFull(false);
		cardIdRef.current = ''; cardByAdminRef.current = false;
	}, []);

	// ── 발표 종료 ────────────────────────────────────────────────────────────
	const stopHost = useCallback(async () => {
		const sid = shareId, roomId = rid, cardId = cardIdRef.current, byAdmin = cardByAdminRef.current;
		const dur = startedAtRef.current ? Math.round((Date.now() - startedAtRef.current) / 1000) : 0;
		teardown();
		if (!authToken || !userId || !sid) return;
		try { await shareStop(authToken, userId, sid); } catch { /* 서버 정리는 실패해도 로컬은 종료 */ }
		// 방 카드를 '종료됨' 으로 갱신 — 남은 사람이 죽은 링크를 누르지 않게
		if (cardId && roomId && username) {
			const marker = buildCallMarker({
				kind: 'call', callId: sid, shareId: sid, state: 'ended', mode: 'roomshare',
				from: username, to: '*', fromName: displayName || username, dur,
			});
			if (byAdmin) { try { await shareCard(authToken, userId, sid, marker, 'end', cardId); } catch { /* 카드 갱신 실패는 무해 */ } }
			else { try { await rcUpdateMessage(authToken, userId, roomId, cardId, marker); } catch { /* 카드 갱신 실패는 무해 */ } }
		}
	}, [authToken, userId, username, displayName, shareId, rid, teardown]);

	const leaveViewer = useCallback(async () => {
		const sid = shareId;
		teardown();
		if (authToken && userId && sid) { try { await shareLeave(authToken, userId, sid); } catch { /* */ } }
	}, [authToken, userId, shareId, teardown]);

	stopRef.current = () => { if (mode === 'host') void stopHost(); else if (mode === 'viewer') void leaveViewer(); };
	// viewerTick 은 아래에서 정의 — 연결 후 감시 폴링(setInterval)이 항상 최신 클로저를 부르도록 렌더마다 갱신

	// ── 발표자 폴링: 대기 중인 시청자에게 offer, answer 수신 시 연결 ────────────
	const hostTick = useCallback(async (sid: string) => {
		if (!authToken || !userId) return;
		const doc: ShareDoc | null = await shareState(authToken, userId, sid).catch(() => null);
		if (!doc) return;
		// 서버가 종료 판정(절전 복귀로 하트비트 2분+ 끊김 등) — 방 카드도 '종료됨' 으로 갱신하며 정리
		if (doc.state !== 'live') { void stopRef.current(); return; }
		const viewers = (doc.viewers || []).filter((v) => v.username && v.state !== 'left');
		setViewerCount(viewers.filter((v) => v.state === 'connected').length);
		for (const v of viewers) {
			const name = String(v.username);
			if (v.state === 'waiting' && pcsRef.current.has(name) && offeredRef.current.has(name)) {
				// 재참여(새로고침 등) — offer 를 이미 실었는데 서버가 waiting 으로 되돌림 = 시청자가 다시 join 한 것.
				//   옛 연결의 disconnect 감지(수초~수십초)를 기다리지 않고 즉시 버리고 새로 offer (2026-08-12 점검 ⑤)
				try { pcsRef.current.get(name)?.close(); } catch { /* */ }
				pcsRef.current.delete(name);
				offeredRef.current.delete(name);
			}
			if (v.state === 'waiting' && !pcsRef.current.has(name)) {
				if (pcsRef.current.size >= MAX_VIEWERS) {
					// 상한 — 'full' 로 표시해 시청자에게 안내가 뜨게 (무언 대기 방지, 2026-08-12 점검 ④)
					void shareDeny(authToken, userId, sid, name).catch(() => undefined);
					continue;
				}
				const stream = streamRef.current;
				if (!stream) continue;
				const pc = new RTCPeerConnection({ iceServers: iceRef.current });
				pcsRef.current.set(name, pc);
				pc.onconnectionstatechange = () => {
					if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
						try { pc.close(); } catch { /* */ }
						pcsRef.current.delete(name);
						offeredRef.current.delete(name);
					}
				};
				stream.getTracks().forEach((tr) => pc.addTrack(tr, stream));
				const offer = await pc.createOffer();
				await pc.setLocalDescription(offer);
				await waitIce(pc);
				try { await shareOffer(authToken, userId, sid, name, JSON.stringify(pc.localDescription)); offeredRef.current.add(name); }
				catch { pcsRef.current.delete(name); try { pc.close(); } catch { /* */ } }
			} else if (v.answer && pcsRef.current.has(name)) {
				const pc = pcsRef.current.get(name)!;
				if (pc.signalingState === 'have-local-offer') {
					try { await pc.setRemoteDescription(JSON.parse(v.answer) as RTCSessionDescriptionInit); }
					catch { /* 잘못된 SDP — 다음 틱에 재시도 대상 아님 */ }
				}
			}
		}
	}, [authToken, userId, teardown]);

	// ── 시청자 폴링: 내 offer 가 도착하면 answer ────────────────────────────
	const viewerTick = useCallback(async (sid: string) => {
		if (!authToken || !userId || !username) return;
		const doc: ShareDoc | null = await shareState(authToken, userId, sid).catch(() => null);
		if (!doc) {
			// 문서 유실(TTL 삭제)·연속 네트워크 오류 — 무한 "연결 중…" 방지 (일시 오류는 카운터 리셋으로 흡수)
			if (++missRef.current >= VIEWER_MISS_MAX) { void tptAlert(t('share.ended', { defaultValue: '화면 공유가 종료되었습니다.' })); teardown(); }
			return;
		}
		missRef.current = 0;
		if (doc.state !== 'live') { void tptAlert(t('share.ended', { defaultValue: '화면 공유가 종료되었습니다.' })); teardown(); return; }
		const me = (doc.viewers || []).find((v) => v.username === username);
		if (me?.state === 'full') {
			// 발표자가 정원 초과로 거절 — 자리가 나면 카드 [참여] 로 다시 시도 가능
			void tptAlert(t('share.full', { defaultValue: '시청 인원이 가득 찼습니다. 잠시 후 다시 시도해 주세요. (최대 {{n}}명)', n: MAX_VIEWERS }));
			teardown(); return;
		}
		if (!me?.offer || viewerPcRef.current) return;
		const pc = new RTCPeerConnection({ iceServers: iceRef.current });
		viewerPcRef.current = pc;
		pc.ontrack = (ev) => {
			const [stream] = ev.streams;
			if (videoRef.current && stream) { videoRef.current.srcObject = stream; void videoRef.current.play().catch(() => undefined); }
			setConnecting(false);
		};
		pc.onconnectionstatechange = () => {
			if (['failed', 'closed'].includes(pc.connectionState)) { void leaveViewer(); }
		};
		try {
			await pc.setRemoteDescription(JSON.parse(me.offer) as RTCSessionDescriptionInit);
			const answer = await pc.createAnswer();
			await pc.setLocalDescription(answer);
			await waitIce(pc);
			await shareAnswer(authToken, userId, sid, JSON.stringify(pc.localDescription));
			// 🕐 연결 후엔 느린 감시 폴링으로 전환 (2026-08-22 사용자 "발표자가 하단 바에서 공유 중단하면
			//   시청자는 멈춘 화면으로 가만히 있음") — 종전 clearPoll(완전 중단)은 종료 신호를 받을 길이 없었고,
			//   원격 종료 시 WebRTC 는 'disconnected' 에 오래 머물러 failed/closed 핸들러도 안 탔다.
			//   3s 폴링이 doc.state='ended' 를 보면 위 상태 체크가 "종료되었습니다" 안내 후 정리한다.
			clearPoll();
			pollRef.current = window.setInterval(() => void viewerTickRef.current(sid), 3000);
		} catch (e) {
			void tptAlert(humanizeError(e, t('share.joinFail', { defaultValue: '화면 공유 참여' })));
			void leaveViewer();
		}
	}, [authToken, userId, username, t, teardown, leaveViewer]);
	viewerTickRef.current = viewerTick;

	// 🤖 안드 셸 과열 대책 D (code 114+, 2026-09-30) — 셸은 백그라운드 60초 뒤 웹뷰 타이머를 멈춘다. 발표·시청 중에는 멈추면 끊기므로 제외 신호.
	useEffect(() => {
		try { window.TPTAndroid?.setBgKeepAlive?.(mode !== 'idle'); } catch { /* 웹/데탑 — 브릿지 없음 */ }
		return () => { try { if (mode !== 'idle') window.TPTAndroid?.setBgKeepAlive?.(false); } catch { /* */ } };
	}, [mode]);

	// ── 발표 시작 (방 헤더 메뉴 → 'tpt:roomshare-start') ─────────────────────
	useEffect(() => {
		const onStart = async (ev: Event) => {
			const d = (ev as CustomEvent<{ roomId?: string }>).detail || {};
			const roomId = String(d.roomId || '');
			if (!roomId || !authToken || !userId || !username) return;
			if (!isTauri()) { void tptAlert(t('share.desktopOnly', { defaultValue: '화면 공유는 PC 앱에서만 사용할 수 있습니다.' })); return; }
			if (mode !== 'idle') { void tptAlert(t('share.already', { defaultValue: '이미 화면 공유가 진행 중입니다.' })); return; }
			let stream: MediaStream;
			try {
				stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
			} catch { return; }   // 사용자가 화면 선택을 취소 — 조용히 종료
			try {
				iceRef.current = await callIceConfig();
				const r = await shareStart(authToken, userId, roomId, displayName || username);
				if (r.busy) {
					stream.getTracks().forEach((tr) => tr.stop());
					void tptAlert(t('share.busy', { defaultValue: '{{host}} 님이 이미 이 대화방에서 화면을 공유하고 있습니다.', host: r.host || '' }));
					return;
				}
				streamRef.current = stream;
				startedAtRef.current = Date.now();
				setShareId(r.shareId); setRid(roomId); setMode('host'); setViewerCount(0);
				// 사용자가 브라우저/OS 의 '공유 중지' 를 누르면 발표 종료
				stream.getVideoTracks()[0]?.addEventListener('ended', () => { void stopRef.current(); });
				// 방에 참여 카드 게시
				const marker = buildCallMarker({
					kind: 'call', callId: r.shareId, shareId: r.shareId, state: 'invite', mode: 'roomshare',
					from: username, to: '*', fromName: displayName || username,
				});
				const saved = await rcSendMessage(authToken, userId, roomId, marker).catch(() => null);
				cardIdRef.current = saved?._id || '';
				// 🖥 읽기전용 방(비공개 토픽 '관리자만 글쓰기') — 본인 명의 게시가 거부되면 서버가 관리자 명의로 대신 올린다 (2026-09-04 사용자
				//   "비공개 토픽인 경우 관리자 외 전체 인원이 다 되게"). 카드가 없으면 시청자가 [참여]할 길이 없다.
				if (!cardIdRef.current) {
					try { const c = await shareCard(authToken, userId, r.shareId, marker, 'post'); if (c.cardId) { cardIdRef.current = c.cardId; cardByAdminRef.current = true; } } catch { /* 카드 없이도 발표는 진행 */ }
				}
				clearPoll();
				// 발표 내내 폴링 유지 — 새 참여자 감지 + 서버 하트비트(폴링이 2분 끊기면 서버가 크래시로 보고 종료 처리.
				//   창 최소화 시 타이머가 분당 1회로 줄어도 하트비트는 유지된다)
				pollRef.current = window.setInterval(() => { void hostTick(r.shareId); }, POLL_MS);
			} catch (e) {
				stream.getTracks().forEach((tr) => tr.stop());
				void tptAlert(humanizeError(e, t('share.startFail', { defaultValue: '화면 공유 시작' })));
			}
		};
		window.addEventListener('tpt:roomshare-start', onStart as EventListener);
		return () => window.removeEventListener('tpt:roomshare-start', onStart as EventListener);
	}, [authToken, userId, username, displayName, mode, t, hostTick]);

	// ── 시청 참여 (카드 [참여] → 'tpt:roomshare-join') ───────────────────────
	useEffect(() => {
		const onJoin = async (ev: Event) => {
			const d = (ev as CustomEvent<{ shareId?: string; hostName?: string }>).detail || {};
			const sid = String(d.shareId || '');
			if (!sid || !authToken || !userId) return;
			if (!isTauri()) { void tptAlert(t('share.desktopOnly', { defaultValue: '화면 공유는 PC 앱에서만 사용할 수 있습니다.' })); return; }
			if (mode !== 'idle') { void tptAlert(t('share.already', { defaultValue: '이미 화면 공유가 진행 중입니다.' })); return; }
			try {
				iceRef.current = await callIceConfig();
				const r = await shareJoin(authToken, userId, sid, displayName || username || '');
				if (r.host) return;   // 발표자 본인이 자기 카드를 누른 경우
				setShareId(sid); setHostName(String(d.hostName || '')); setMode('viewer'); setConnecting(true);
				clearPoll();
				pollRef.current = window.setInterval(() => void viewerTick(sid), POLL_MS);
				void viewerTick(sid);
			} catch (e) {
				void tptAlert(humanizeError(e, t('share.joinFail', { defaultValue: '화면 공유 참여' })));
			}
		};
		window.addEventListener('tpt:roomshare-join', onJoin as EventListener);
		return () => window.removeEventListener('tpt:roomshare-join', onJoin as EventListener);
	}, [authToken, userId, username, displayName, mode, t, viewerTick]);

	// 🪟 별도 창(/share) — 뜨자마자 그 공유에 참여. 위 join 리스너가 이미 등록된 뒤에 발사되도록 이 자리에 둔다.
	//   창을 새로고침해도 같은 shareId 로 다시 붙는다(서버가 뷰어 상태를 들고 있음).
	const soloFiredRef = useRef(false);
	useEffect(() => {
		if (!solo?.shareId || soloFiredRef.current || mode !== 'idle') return;
		soloFiredRef.current = true;
		window.dispatchEvent(new CustomEvent('tpt:roomshare-join', { detail: { shareId: solo.shareId, hostName: solo.hostName } }));
	}, [solo, mode]);

	// 언마운트/로그아웃 정리
	useEffect(() => () => { teardown(); }, [teardown]);

	if (mode === 'idle') {
		// 별도 창인데 아직 붙지 않았거나 종료된 상태 — 빈 화면 대신 안내를 남긴다.
		if (!solo) return null;
		return (
			<div className="flex h-[100dvh] items-center justify-center bg-black px-6 text-center text-ui-sm text-white/60">
				{soloFiredRef.current
					? t('share.ended', { defaultValue: '화면 공유가 종료되었습니다.' })
					: t('share.connecting', { defaultValue: '연결 중…' })}
			</div>
		);
	}

	// 발표자 — 작은 상태 바 (화면을 가리지 않게 우하단)
	if (mode === 'host') {
		return (
			<div className="fixed bottom-4 right-4 z-[1200] flex items-center gap-3 rounded-xl border border-[var(--tpt-color-divider)] bg-[var(--tpt-color-bg-sidebar)] px-4 py-3 shadow-2xl"
				data-testid="roomshare-host-bar">
				<span className="relative flex h-2.5 w-2.5">
					<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
					<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
				</span>
				<span className="text-ui-sm font-semibold text-fg">{t('share.sharing', { defaultValue: '화면 공유 중' })}</span>
				<span className="text-ui-xs text-fg-muted">{t('share.viewers', { defaultValue: '보는 사람 {{n}}명', n: viewerCount })}</span>
				<button type="button" onClick={() => void stopHost()} data-testid="roomshare-stop"
					className="rounded-lg bg-red-500 px-3 py-1.5 text-ui-xs font-semibold text-white hover:brightness-110">
					{t('share.stop', { defaultValue: '공유 종료' })}
				</button>
			</div>
		);
	}

	// 시청자 — 화면 뷰어. solo(별도 창)면 창 전체를 채운다.
	return (
		<div className={solo
			? 'flex h-[100dvh] w-full flex-col bg-black'
			: full
				? 'fixed inset-0 z-[1200] flex flex-col bg-black'
				: 'fixed bottom-4 right-4 z-[1200] flex w-[min(560px,46vw)] flex-col overflow-hidden rounded-xl border border-[var(--tpt-color-divider)] bg-black shadow-2xl'}
			data-testid="roomshare-viewer">
			<div className="flex items-center gap-2 bg-black/80 px-3 py-2">
				<Icon name="monitor" size={14} />
				<span className="truncate text-ui-xs font-semibold text-white">
					{hostName ? t('share.hostSharing', { defaultValue: '{{name}} 님의 화면', name: hostName }) : t('share.sharingTitle', { defaultValue: '화면 공유' })}
				</span>
				{connecting && <span className="text-ui-xs text-white/70">{t('share.connecting', { defaultValue: '연결 중…' })}</span>}
				<div className="ml-auto flex items-center gap-1">
					{!solo && (
						<button type="button" onClick={() => setFull((v) => !v)} title={t('share.fullscreen', { defaultValue: '전체 화면' })}
							className="rounded px-2 py-1 text-ui-xs text-white/80 hover:bg-white/10">
							{full ? t('share.exitFullscreen', { defaultValue: '작게' }) : t('share.fullscreen', { defaultValue: '전체 화면' })}
						</button>
					)}
					<button type="button"
						onClick={() => { void leaveViewer(); if (solo) { try { window.close(); } catch { /* 정책상 못 닫으면 안내 화면 */ } } }}
						data-testid="roomshare-leave"
						className="rounded bg-white/10 px-2 py-1 text-ui-xs font-semibold text-white hover:bg-white/20">
						{t('share.leave', { defaultValue: '나가기' })}
					</button>
					{/* ✕ = 작게(시청은 유지) — 전체 화면 오버레이일 때만 (2026-10-08) */}
					{full && !solo && (
						<button type="button" onClick={() => setFull(false)} aria-label={t('share.exitFullscreen', { defaultValue: '작게' }) as string} data-testid="roomshare-close"
							className="rounded px-2 py-1 text-ui-xs font-semibold text-white/80 hover:bg-white/10">✕</button>
					)}
				</div>
			</div>
			{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
			<video ref={videoRef} autoPlay playsInline muted className={(solo || full) ? 'min-h-0 flex-1 bg-black object-contain' : 'aspect-video w-full bg-black object-contain'} />
		</div>
	);
}
